import { Button } from "@/components/ui/button";
import { ContentCard } from "@/components/ui/ContentCard";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { BookOpen, CheckCircle2, TrendingUp } from "lucide-react";
import { type Incident, effectiveGrade } from "./types";
import { ISSRestorativeTasks } from "./ISSRestorativeTasks";

interface ISSDocsStageProps {
  incident: Incident;
  onUpdate: (fields: Partial<Incident>) => void;
  onBack: () => void;
  onClose: () => void;
  onGoToDashboard: () => void;
  isReadOnly?: boolean;
}

export const ISSDocsStage = ({
  incident,
  onUpdate,
  onBack,
  onClose,
  onGoToDashboard,
  isReadOnly = false,
}: ISSDocsStageProps) => {
  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* ISS / Restorative Tasks */}
      <ISSRestorativeTasks
        gradeLevel={effectiveGrade(incident.grade_level)}
        incidentDescription={incident.incident_description}
        studentReflection={incident.student_reflection_responses}
        studentIdentifier={incident.student_identifier}
      />

      {/* ISS Academic Credit Plan */}
      <ContentCard>
        <div className="flex items-start gap-3 mb-4">
          <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0 mt-0.5">
            <BookOpen className="w-4 h-4 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-800 font-literata">
              ISS Academic Credit Plan
            </h2>
            <p className="text-sm text-gray-600 mt-0.5">
              Students removed from class must have the opportunity to earn full credit
              for work completed during removal.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <Switch
              checked={incident.iss_reflection_completed}
              disabled={isReadOnly}
              onCheckedChange={(v) =>
                !isReadOnly && onUpdate({ iss_reflection_completed: v, status: v ? "closed" : "followup" })
              }
            />
            <span className="text-sm text-gray-700">
              Academic work plan has been communicated to the student's teachers
            </span>
          </label>

          <div className="space-y-1.5">
            <Label>Work plan details</Label>
            <p className="text-xs text-gray-500">
              List each class, the work assigned, and how the student will submit or complete it.
            </p>
            <Textarea
              placeholder={"Example format:\n• Period 1 — Ms. Johnson (Math): Ch. 4 worksheet, submit to office by end of day\n• Period 2 — Mr. Rivera (English): Read pp. 45–60, written response\n• Period 3 — Ms. Lee (Science): Lab questions from today — teacher will provide copy"}
              rows={5}
              value={incident.iss_work_plan}
              disabled={isReadOnly}
              onChange={(e) => onUpdate({ iss_work_plan: e.target.value })}
            />
          </div>
        </div>
      </ContentCard>

      {/* Return to Class Plan */}
      <ContentCard>
        <div className="flex items-start gap-3 mb-4">
          <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0 mt-0.5">
            <TrendingUp className="w-4 h-4 text-green-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-800 font-literata">
              Return to Class Plan
            </h2>
            <p className="text-sm text-gray-600 mt-0.5">
              A documented re-entry plan significantly reduces the chance of re-referral.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <Switch
              checked={incident.reentry_conversation_needed}
              disabled={isReadOnly}
              onCheckedChange={(v) => !isReadOnly && onUpdate({ reentry_conversation_needed: v })}
            />
            <span className="text-sm text-gray-700">
              Re-entry conversation needed before student returns to class
            </span>
          </label>

          {incident.reentry_conversation_needed && (
            <div className="space-y-1.5">
              <Label>Who will facilitate re-entry conversation</Label>
              <input
                type="text"
                className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-bodhi-teal"
                placeholder="e.g. AP, counselor, or classroom teacher"
                value={incident.reentry_facilitator}
                disabled={isReadOnly}
                onChange={(e) => onUpdate({ reentry_facilitator: e.target.value })}
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Return-to-class conditions or modifications</Label>
            <p className="text-xs text-gray-500">
              Any classroom adjustments, seating changes, or temporary agreements.
            </p>
            <Textarea
              placeholder={"Examples:\n• Seat moved away from [student]\n• Student may take a movement break with pass\n• Teacher will check in privately within first 10 minutes"}
              rows={3}
              value={incident.return_to_class_plan}
              disabled={isReadOnly}
              onChange={(e) => onUpdate({ return_to_class_plan: e.target.value })}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Check-in schedule after return</Label>
            <input
              type="text"
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-bodhi-teal"
              placeholder="e.g. Daily check-in for 1 week, then weekly through end of quarter"
              value={incident.reentry_checkin_schedule}
              disabled={isReadOnly}
              onChange={(e) => onUpdate({ reentry_checkin_schedule: e.target.value })}
            />
          </div>
        </div>
      </ContentCard>

      {/* Nav */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          className="border-gray-300 text-gray-700 bg-white hover:bg-gray-100 hover:text-gray-900"
          onClick={onBack}
        >
          ← Back to Resolution
        </Button>
        {incident.status !== "closed" && !isReadOnly && (
          <Button
            className="bg-bodhi-teal text-white hover:bg-bodhi-teal/90"
            onClick={onClose}
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Mark Incident Complete
          </Button>
        )}
        {incident.status === "closed" && (
          <Button
            variant="outline"
            className="border-gray-300 text-gray-700 bg-white hover:bg-gray-100 hover:text-gray-900"
            onClick={onGoToDashboard}
          >
            Back to Dashboard
          </Button>
        )}
      </div>
    </div>
  );
};
